import { VoiceConfig as VoiceConfigInterface } from "@baiki/interfaces";

// Re-export for backwards compatibility
export type VoiceConfig = VoiceConfigInterface;

// Unified ElevenLabs voice settings used across all languages.
// `speed` is honored by multilingual_v2; eleven_v3 ignores it.
//
// `stability` on eleven_v3 selects one of three modes, not a point on a slider:
// 0.0 "Creative", 0.5 "Natural", 1.0 "Robust". Creative is documented as prone
// to hallucination and was caught dropping six words mid-sentence from a
// narration, so we run Natural. The backend re-floors this anyway (see
// `normalizeVoiceSettings` in functions' elevenlabs.ts) — clients only propose.
const SHARED_VOICE_SETTINGS = {
  similarityBoost: 0.75,
  stability: 0.5,
  style: 0.5,
  speed: 1,
  useSpeakerBoost: true,
} as const;

const SHARED_MODEL_ID = "eleven_v3";

export const DEFAULT_VOICE_CONFIG: VoiceConfig = {
  voiceId: "acCWxmzPBgXdHwA63uzP",
  voiceName: "Nichalia - Gentle, Kind and Sweet",
  modelId: SHARED_MODEL_ID,
  voiceSettings: { ...SHARED_VOICE_SETTINGS },
};

/**
 * Language-specific voice configurations for ElevenLabs.
 * Voice IDs differ per language; modelId and voiceSettings are
 * unified across all languages — only the voice itself changes.
 *
 * To customize a voice for a specific language:
 * 1. Get voice IDs from ElevenLabs dashboard
 * 2. Update the voiceId and voiceName for the language
 */
export const LANGUAGE_VOICE_CONFIG: Record<string, VoiceConfig> = {
  en: {
    voiceId: "acCWxmzPBgXdHwA63uzP",
    voiceName: "Nichalia - Gentle, Kind and Sweet",
    modelId: SHARED_MODEL_ID,
    voiceSettings: { ...SHARED_VOICE_SETTINGS },
  },
  pl: {
    voiceId: "acCWxmzPBgXdHwA63uzP",
    voiceName: "Nichalia - Gentle, Kind and Sweet",
    modelId: SHARED_MODEL_ID,
    voiceSettings: { ...SHARED_VOICE_SETTINGS },
  },
  es: {
    voiceId: "acCWxmzPBgXdHwA63uzP",
    voiceName: "Nichalia - Gentle, Kind and Sweet",
    modelId: SHARED_MODEL_ID,
    voiceSettings: { ...SHARED_VOICE_SETTINGS },
  },
  it: {
    voiceId: "acCWxmzPBgXdHwA63uzP",
    voiceName: "Nichalia - Gentle, Kind and Sweet",
    modelId: SHARED_MODEL_ID,
    voiceSettings: { ...SHARED_VOICE_SETTINGS },
  },
  fr: {
    voiceId: "acCWxmzPBgXdHwA63uzP",
    voiceName: "Nichalia - Gentle, Kind and Sweet",
    modelId: SHARED_MODEL_ID,
    voiceSettings: { ...SHARED_VOICE_SETTINGS },
  },
  de: {
    voiceId: "acCWxmzPBgXdHwA63uzP",
    voiceName: "Nichalia - Gentle, Kind and Sweet",
    modelId: SHARED_MODEL_ID,
    voiceSettings: { ...SHARED_VOICE_SETTINGS },
  },
};

/**
 * Get the appropriate voice configuration for a given language
 * @param language - Language code (e.g., "en", "pl", "es")
 * @returns Voice configuration for the language, or default if not found
 */
export function getVoiceConfigForLanguage(language: string): VoiceConfig {
  const config = LANGUAGE_VOICE_CONFIG[language];
  if (config) {
    console.log(
      `[VoiceConfig] Using voice for language '${language}': ${config.voiceName}`,
    );
    return config;
  }
  console.log(
    `[VoiceConfig] No specific voice for language '${language}', using default`,
  );
  return DEFAULT_VOICE_CONFIG;
}
